import * as THREE from 'three';

import store from "../../../../../../../../store";
import {
  setSQFTActiveShapeId,
  setSQFTSystemDeleteMessage,
} from "../../../../../../../../actions/sqftMenuFunctions";
import {
  CABINET_BATHROOM_LABELS,
  CABINET_COMMON_COLOR_PART_IDS,
  SIDEBAR_BUTTONS_MENU,
  SQFT_VIEW_MODE,
} from "../../../../../../constants";
import workspace from "../../../../../canvas/core/workspace";
import SqftStore from "../../../../../canvas/store/sqftStore";
import workspaceControllers from "../../../../../canvas/store/workspaceControllers";
import sqftShapeIntersectionsManager from "../../../../../canvas/store/sqftShapeIntersectionsManager";
import { CabinetsSet } from "../../../../../../helpers/sqftCabinetsDataMapper";
import { SECTION_LAYOUT_ID_TAG } from "../modelsLoader/cabinetInterior/tallCabinetSectionHighlightBoxes";
import {
  CABINET_PLACEMENT_TYPE,
  applyCabinetLineSnapPlacement,
  createCabinetPlacementContext,
  getShapeStartBounds,
  resolveCabinetPlacement,
} from "./cabinetPlacementResolver";
import {
  CABINET_FREE_DRAG_BOUNDARY_WARNING_DISTANCE,
  CabinetFreeDragBoundaryPreview,
  clampCabinetPositionToLayoutBounds,
  getCabinetFreeDragLayoutBounds,
  getMovedShapeBounds,
  isBoundsNearLayoutBoundary,
  isSameWorkspacePosition,
} from "./cabinetFreeDragBoundary";
import CountertopDragPreviewController from "./CountertopDragPreviewController";
import PlacementPreviewPlate from "./placementPreviewPlate";

const DRAG_THRESHOLD_PX = 5;
const COUNTERTOP_RELOAD_DELAY_MS = 250;
const DRAGGABLE_CABINET_PART_IDS = [
  ...CABINET_COMMON_COLOR_PART_IDS,
  ...Object.keys(CABINET_BATHROOM_LABELS),
];

/**
 * Lets users drag cabinet groups directly in 3D while keeping the 2D workspace
 * as the source of truth for placement, snapping, calculations, and saving.
 */
export default class CabinetPlacementDragControls {
  constructor(threeScene, scene, camera, domElement, getSelectableGroups) {
    this.threeScene = threeScene;
    this.scene = scene;
    this.camera = camera;
    this.domElement = domElement;
    this.getGroups = getSelectableGroups;

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this.planePoint = new THREE.Vector3();

    this.pendingDrag = null;
    this.activeDrag = null;
    this.pendingMoveFrameId = null;
    this.pendingMoveEvent = null;
    this.controlsWereEnabled = true;
    this.placementPreview = new PlacementPreviewPlate(scene);
    this.countertopReloadTimer = null;
    this.countertopReloadRequestId = 0;
    this.isCountertopReloading = false;
    this.hasQueuedCountertopReload = false;
    this.countertopDragPreview = new CountertopDragPreviewController(threeScene);
    this.freeDragBoundaryPreview = new CabinetFreeDragBoundaryPreview(scene);

    this.domElement.addEventListener('pointerdown', this._onPointerDown, false);
    this.domElement.addEventListener('pointermove', this._onPointerMove, false);
    this.domElement.addEventListener('pointerup', this._onPointerUp, false);
    this.domElement.addEventListener('pointercancel', this._onPointerCancel, false);
  }

  dispose() {
    this.domElement.removeEventListener('pointerdown', this._onPointerDown);
    this.domElement.removeEventListener('pointermove', this._onPointerMove);
    this.domElement.removeEventListener('pointerup', this._onPointerUp);
    this.domElement.removeEventListener('pointercancel', this._onPointerCancel);
    if (this.countertopReloadTimer) {
      clearTimeout(this.countertopReloadTimer);
    }
    this._resetDrag();
    this.placementPreview.dispose();
    this.freeDragBoundaryPreview.dispose();
  }

  _disposePlacementPreview() {
    this.placementPreview.clear();
  }

  _setPointerFromEvent(event) {
    const rect = this.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
  }

  _findCabinetGroup(object, groups) {
    let current = object;

    while (current && current.parent && !groups.includes(current)) {
      current = current.parent;
    }

    return groups.includes(current) ? current : null;
  }

  _getHitInfo(event) {
    this._setPointerFromEvent(event);

    const groups = this.getGroups();
    const hits = this.raycaster.intersectObjects(groups, true);
    if (!hits.length) {
      return null;
    }

    const group = this._findCabinetGroup(hits[0].object, groups);
    if (!group || !group.userData || !DRAGGABLE_CABINET_PART_IDS.includes(group.userData.partId)) {
      return null;
    }

    // Tall-cabinet section dragging is handled by ShapeContextMenuControls only
    // while the cabinet attributes panel is open for this shape.
    if (!this._isDeleteMode()
      && this._hasSectionLayoutId(hits[0].object, group)
      && this._canDragSectionsForGroup(group)
    ) {
      return null;
    }

    return {
      group,
      point: hits[0].point,
    };
  }

  _isDeleteMode() {
    return store.getState().sqftMenuState.activeButton === SIDEBAR_BUTTONS_MENU.delete;
  }

  _hasSectionLayoutId(object, rootGroup) {
    let current = object;

    while (current && current !== rootGroup.parent) {
      if (current.userData && current.userData[SECTION_LAYOUT_ID_TAG]) {
        return true;
      }

      if (current === rootGroup) {
        return false;
      }

      current = current.parent;
    }

    return false;
  }

  _canDragSectionsForGroup(group) {
    const shapeId = group && group.userData && group.userData.shapeId;
    if (!shapeId) {
      return false;
    }

    const shape = workspace.getShapeById(shapeId);

    return Boolean(
      store.getState().sqftMenuState.sqftActiveShapeId === shapeId
      && shape
      && shape.shapeCabinet
      && shape.shapeCabinet.getDoorExtensionEnabled()
    );
  }

  _shouldSetActiveShapeAfterDrag(shapeId) {
    const activeShapeId = store.getState().sqftMenuState.sqftActiveShapeId;
    return this.threeScene.isMobile || activeShapeId === shapeId;
  }

  _intersectDragPlane(event) {
    this._setPointerFromEvent(event);
    if (!this.raycaster.ray.intersectPlane(this.dragPlane, this.planePoint)) {
      return null;
    }

    return this.planePoint.clone();
  }

  _setOrbitControlsEnabled(isEnabled) {
    if (this.threeScene && this.threeScene.controls) {
      this.threeScene.controls.enabled = isEnabled;
    }
  }

  _setCursor(cursor) {
    if (this.domElement.style.cursor !== cursor) {
      this.domElement.style.cursor = cursor;
    }
  }

  _capturePointer(event) {
    if (!this.domElement.setPointerCapture) {
      return;
    }

    try {
      this.domElement.setPointerCapture(event.pointerId);
    } catch (error) {
      void error;
    }
  }

  _releasePointer(event) {
    if (!this.domElement.releasePointerCapture) {
      return;
    }

    try {
      this.domElement.releasePointerCapture(event.pointerId);
    } catch (error) {
      void error;
    }
  }

  _resetDrag() {
    this._cancelPendingMovePreview();
    this.pendingDrag = null;
    this.activeDrag = null;
    this._disposePlacementPreview();
    this.freeDragBoundaryPreview.clear();
    this._setCursor('');
    this._setOrbitControlsEnabled(this.controlsWereEnabled);
  }

  _rollbackPreview(drag) {
    if (!drag || !drag.group) {
      return;
    }

    drag.group.position.copy(drag.startGroupPosition);
    this.countertopDragPreview.resetTargets(drag.countertopPreviewTargets);
  }

  _startActiveDrag(event) {
    const startPointOnPlane = this._intersectDragPlane({
      clientX: this.pendingDrag.startClientX,
      clientY: this.pendingDrag.startClientY,
    });

    if (!startPointOnPlane) {
      this._resetDrag();
      return;
    }

    this.activeDrag = {
      ...this.pendingDrag,
      startPointOnPlane,
      lastDelta: new THREE.Vector3(),
      freeDragLayoutBounds: getCabinetFreeDragLayoutBounds(this.pendingDrag.shape),
      placementContext: createCabinetPlacementContext(this.pendingDrag.shape),
      countertopPreviewTargets: this.countertopDragPreview.captureTargets(this.pendingDrag.shape),
    };

    this._setCursor('grabbing');
    this._movePreview(event);
  }

  _movePreview(event) {
    if (!this.activeDrag) {
      return;
    }

    const pointOnPlane = this._intersectDragPlane(event);
    if (!pointOnPlane) {
      return;
    }

    const delta = pointOnPlane.sub(this.activeDrag.startPointOnPlane);
    this.activeDrag.lastDelta.copy(delta);
    const rawCandidatePosition = {
      x: this.activeDrag.startShapePosition.x + (delta.x / (this.threeScene.modelScale || 1)),
      y: this.activeDrag.startShapePosition.y + (delta.z / (this.threeScene.modelScale || 1)),
    };
    const placement = resolveCabinetPlacement(
      this.activeDrag.shape,
      rawCandidatePosition,
      this.activeDrag.startShapePosition,
      this.activeDrag.startShapeBounds,
      this.activeDrag.placementContext
    );
    const clampedFreeDragPosition = clampCabinetPositionToLayoutBounds(
      rawCandidatePosition,
      this.activeDrag.startShapePosition,
      this.activeDrag.startShapeBounds,
      this.activeDrag.freeDragLayoutBounds
    );
    const previewPosition = placement
      ? placement.position
      : clampedFreeDragPosition;
    const previewDelta = {
      x: (previewPosition.x - this.activeDrag.startShapePosition.x) * (this.threeScene.modelScale || 1),
      z: (previewPosition.y - this.activeDrag.startShapePosition.y) * (this.threeScene.modelScale || 1),
    };

    this.activeDrag.placement = placement;
    this.activeDrag.group.position.set(
      this.activeDrag.startGroupPosition.x + previewDelta.x,
      this.activeDrag.startGroupPosition.y,
      this.activeDrag.startGroupPosition.z + previewDelta.z
    );
    this.countertopDragPreview.moveTargets(this.activeDrag.countertopPreviewTargets, previewDelta);
    this._updatePlacementPreview(placement, this.activeDrag.group);
    this._updateFreeDragBoundaryPreview(placement, rawCandidatePosition, clampedFreeDragPosition);
  }

  _cancelPendingMovePreview() {
    if (this.pendingMoveFrameId) {
      window.cancelAnimationFrame(this.pendingMoveFrameId);
      this.pendingMoveFrameId = null;
    }

    this.pendingMoveEvent = null;
  }

  _flushPendingMovePreview() {
    // Pointerup must commit the latest cursor position, even if the last queued
    // animation frame has not rendered yet.
    if (!this.pendingMoveEvent) {
      return;
    }

    const event = this.pendingMoveEvent;

    this._cancelPendingMovePreview();
    this._movePreview(event);
  }

  _queueMovePreview(event) {
    // Pointer events can arrive faster than the scene can render. Keep only the
    // latest cursor position per frame so snap resolution does not stack up.
    this.pendingMoveEvent = {
      clientX: event.clientX,
      clientY: event.clientY,
    };

    if (this.pendingMoveFrameId) {
      return;
    }

    this.pendingMoveFrameId = window.requestAnimationFrame(() => {
      this.pendingMoveFrameId = null;
      this._flushPendingMovePreview();
    });
  }

  /**
   * Draws a glossy target plate for the current placement candidate.
   * Green means plain edge snap; red means insertion that will shift neighbours.
   */
  _updatePlacementPreview(placement, movingGroup) {
    if (!placement || !placement.hoveredLine) {
      this._disposePlacementPreview();
      return;
    }

    const modelScale = this.threeScene.modelScale || 1;
    const targetGroup = this._findGroupByShapeId(placement.targetShape.id);

    this.placementPreview.update({
      placement,
      modelScale,
      targetObject: targetGroup,
      fallbackObject: movingGroup,
    });
  }

  _findGroupByShapeId(shapeId) {
    return (this.getGroups() || []).find(group => group.userData && group.userData.shapeId === shapeId) || null;
  }

  async _commitDrag(drag) {
    if (!drag || !drag.group || !drag.group.userData.shapeId) {
      return;
    }

    const shape = workspace.getShapeById(drag.group.userData.shapeId);
    if (!shape || typeof shape.getPosition !== 'function' || typeof shape.setPosition !== 'function') {
      this._rollbackPreview(drag);
      return;
    }

    const modelScale = this.threeScene.modelScale || 1;
    const shouldSyncCountertops = this.countertopDragPreview.canSyncForShape(shape);
    const previousCountertopPositions = this.countertopDragPreview.getCurrentPositions(shape);
    const shouldCaptureAutoShuffleStart = shape.shapeAutoShuffle
      && typeof shape.shapeAutoShuffle.captureDragStartState === 'function';

    // 3D clears snap metadata before commit, so preserve the original run first.
    // Same-run reorder depends on this to avoid shifting cabinets after the
    // dragged cabinet's original slot.
    if (shouldCaptureAutoShuffleStart) {
      shape.shapeAutoShuffle.captureDragStartState();
    }

    try {
      if (shape.shapeAbsolute) {
        shape.shapeAbsolute.setAbsolutePos(null);
        shape.shapeAbsolute.setSnappedWithShape(null);
        shape.shapeAbsolute.setSnappedShapeGroup(null);
      }

      this._applyPlacementToShape(shape, drag, modelScale);
    } finally {
      if (
        shouldCaptureAutoShuffleStart
        && typeof shape.shapeAutoShuffle.clearDragStartState === 'function'
      ) {
        shape.shapeAutoShuffle.clearDragStartState();
      }
    }

    sqftShapeIntersectionsManager.updateShapesIntersections();
    workspaceControllers.snappedShapeGroups.updateShapesSnappedGroups(false);
    SqftStore.updateCalculations();
    if (this._shouldSetActiveShapeAfterDrag(drag.group.userData.shapeId)) {
      store.dispatch(setSQFTActiveShapeId(drag.group.userData.shapeId));
    }

    this._syncCabinetTransforms();

    if (shouldSyncCountertops) {
      const shouldReloadCountertops = this.countertopDragPreview.syncAfterCommit(
        previousCountertopPositions,
        drag.countertopPreviewTargets
      );
      if (shouldReloadCountertops) {
        this._queueCountertopReload(0);
      }
    }
  }

  /**
   * Applies the final workspace position.
   * INSERT delegates to full auto-shuffle and may move neighbours.
   * SNAP delegates to alignment only, attaching to the edge without moving the run.
   * Free drag falls back to raw 3D plane movement.
   */
  _applyPlacementToShape(shape, drag, modelScale) {
    const placement = drag.placement;

    if (
      placement
      && placement.type === CABINET_PLACEMENT_TYPE.INSERT
      && shape.shapeAutoShuffle
      && typeof shape.shapeAutoShuffle.performFullAutoShuffle === 'function'
    ) {
      shape.shapeAutoShuffle.performFullAutoShuffle(
        placement.insertPoint,
        placement.hoveredLine,
        placement.targetShape
      );
      return;
    }

    if (
      placement
      && placement.edge
      && (
        placement.edge.role === 'back-wall'
        || placement.edge.role === 'side-wall'
      )
    ) {
      applyCabinetLineSnapPlacement(shape, placement);
      return;
    }

    if (
      placement
      && placement.type === CABINET_PLACEMENT_TYPE.SNAP
      && shape.shapeAutoShuffle
      && typeof shape.shapeAutoShuffle.alignShapeToExistingShapes === 'function'
    ) {
      shape.shapeAutoShuffle.alignShapeToExistingShapes(
        placement.position,
        placement.hoveredLine,
        placement.targetShape
      );
      return;
    }

    const nextPosition = placement
      ? placement.position
      : clampCabinetPositionToLayoutBounds({
        x: drag.startShapePosition.x + (drag.lastDelta.x / modelScale),
        y: drag.startShapePosition.y + (drag.lastDelta.z / modelScale),
      }, drag.startShapePosition, drag.startShapeBounds, drag.freeDragLayoutBounds);

    shape.setPosition(nextPosition);
  }

  _updateFreeDragBoundaryPreview(placement, rawCandidatePosition, clampedFreeDragPosition) {
    if (placement || !this.activeDrag) {
      this.freeDragBoundaryPreview.clear();
      return;
    }

    const clampedBounds = getMovedShapeBounds(
      clampedFreeDragPosition,
      this.activeDrag.startShapePosition,
      this.activeDrag.startShapeBounds
    );
    const shouldShow = !isSameWorkspacePosition(rawCandidatePosition, clampedFreeDragPosition)
      || isBoundsNearLayoutBoundary(
        clampedBounds,
        this.activeDrag.freeDragLayoutBounds,
        CABINET_FREE_DRAG_BOUNDARY_WARNING_DISTANCE
      );

    this.freeDragBoundaryPreview.update({
      bounds: this.activeDrag.freeDragLayoutBounds,
      modelScale: this.threeScene.modelScale || 1,
      shouldShow,
    });
  }

  /**
   * Cabinet GLB geometry does not change during a move, so dragging only needs
   * to sync existing instance transforms from the updated workspace state.
   */
  async _syncCabinetTransforms() {
    const modelsLoader = this.threeScene && this.threeScene.modelsLoader;
    if (!modelsLoader) {
      return;
    }

    const shouldDisplayFramedDoors = store.getState().sqftState.SQFTViewMode === SQFT_VIEW_MODE.threeDModeFramed;
    const cabinetPositions = await CabinetsSet.prepareCabinetsPositions(shouldDisplayFramedDoors);
    modelsLoader.updateModelTransforms('cabinets', cabinetPositions);
  }

  /**
   * Countertop geometry can change after cabinet movement because snapped groups
   * and overhangs can change. Reloads are debounced and collapsed to the latest
   * drag so quick repeated moves do not run one countertop rebuild per drop.
   */
  _queueCountertopReload(delay = COUNTERTOP_RELOAD_DELAY_MS) {
    this.countertopReloadRequestId += 1;
    const requestId = this.countertopReloadRequestId;

    if (this.countertopReloadTimer) {
      clearTimeout(this.countertopReloadTimer);
    }

    this.countertopReloadTimer = setTimeout(() => {
      this._runCountertopReload(requestId);
    }, delay);
  }

  async _runCountertopReload(requestId) {
    if (requestId !== this.countertopReloadRequestId) {
      return;
    }

    if (this.isCountertopReloading) {
      this.hasQueuedCountertopReload = true;
      return;
    }

    const modelsLoader = this.threeScene && this.threeScene.modelsLoader;
    if (!modelsLoader) {
      return;
    }

    this.isCountertopReloading = true;

    try {
      await modelsLoader.refreshCountertops({
        shouldContinue: () => requestId === this.countertopReloadRequestId,
      });
    } catch (error) {
      console.error('[CabinetPlacementDragControls] Could not reload countertops after cabinet drag:', error);
    } finally {
      this.isCountertopReloading = false;

      if (this.hasQueuedCountertopReload || requestId !== this.countertopReloadRequestId) {
        this.hasQueuedCountertopReload = false;
        this._queueCountertopReload();
      }
    }
  }

  _onPointerDown = event => {
    if (event.button !== 0) {
      return;
    }

    const hitInfo = this._getHitInfo(event);
    if (!hitInfo || !hitInfo.group.userData.shapeId) {
      return;
    }

    if (this._isDeleteMode()) {
      const shape = workspace.getShapeById(hitInfo.group.userData.shapeId);
      if (shape) {
        store.dispatch(setSQFTSystemDeleteMessage({isOpened: true, shape}));
      }
      return;
    }

    if (this.threeScene && typeof this.threeScene.refreshCabinetLighting === 'function') {
      this.threeScene.refreshCabinetLighting();
    }

    const shape = workspace.getShapeById(hitInfo.group.userData.shapeId);
    if (!shape || typeof shape.getPosition !== 'function') {
      return;
    }

    this.controlsWereEnabled = !this.threeScene || !this.threeScene.controls || this.threeScene.controls.enabled;
    this._setOrbitControlsEnabled(false);
    this._capturePointer(event);

    this.dragPlane.set(new THREE.Vector3(0, 1, 0), -hitInfo.group.position.y);
    this.pendingDrag = {
      group: hitInfo.group,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startGroupPosition: hitInfo.group.position.clone(),
      startShapePosition: { ...shape.getPosition() },
      startShapeBounds: getShapeStartBounds(shape),
      shape,
    };
  }

  _onPointerMove = event => {
    if (!this.pendingDrag) {
      const hitInfo = this._getHitInfo(event);
      this._setCursor(hitInfo ? (this._isDeleteMode() ? 'pointer' : 'grab') : '');
      return;
    }

    if (event.pointerId !== this.pendingDrag.pointerId) {
      return;
    }

    const distanceX = event.clientX - this.pendingDrag.startClientX;
    const distanceY = event.clientY - this.pendingDrag.startClientY;
    const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

    if (!this.activeDrag && distance >= DRAG_THRESHOLD_PX) {
      this._startActiveDrag(event);
    } else if (this.activeDrag) {
      this._queueMovePreview(event);
    }

    if (this.activeDrag) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  _onPointerUp = event => {
    if (!this.pendingDrag || event.pointerId !== this.pendingDrag.pointerId) {
      return;
    }

    const drag = this.activeDrag;
    this._releasePointer(event);

    if (drag) {
      this._flushPendingMovePreview();
      event.preventDefault();
      event.stopPropagation();
      this._commitDrag(drag).catch((error) => {
        console.error('[CabinetPlacementDragControls] Could not commit cabinet drag:', error);
        this._rollbackPreview(drag);
      });
    }

    this._resetDrag();
  }

  _onPointerCancel = event => {
    this._cancelPendingMovePreview();

    if (this.activeDrag) {
      this._rollbackPreview(this.activeDrag);
    }

    this._releasePointer(event);
    this._resetDrag();
  }
}

